import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileCatExpanded, setMobileCatExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCategoryClick = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const textColor = isScrolled ? "text-foreground-950" : "text-background-50";
  const textMuted = isScrolled ? "text-foreground-700 hover:text-foreground-950" : "text-background-50/80 hover:text-background-50";
  const borderColor = isScrolled ? "border-background-300" : "border-background-50/30";
  const outlineBtnHover = isScrolled ? "hover:bg-background-100" : "hover:bg-background-50/10";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="px-4 md:px-6 py-4 flex items-center justify-between">
        <Link
          to="/"
          className={`font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap cursor-pointer ${textColor}`}
        >
          OOPBuy <span className={`italic font-normal ${isScrolled ? "text-primary-500" : "text-background-50/70"}`}>Spreadsheets</span>
        </Link>

        <div className="hidden md:flex items-center gap-5">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${textMuted}`}
          >
            Home
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${textMuted}`}
            >
              Categories
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-2 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/article/oopbuy-spreadsheet-complete-guide-2026"
            className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${textMuted}`}
          >
            Shopping Guide
          </Link>

          <button
            onClick={() => window.open("https://kaspreadsheet.com/", "_blank", "noopener,noreferrer")}
            className={`px-4 py-2 text-sm font-medium border rounded-md transition-colors cursor-pointer whitespace-nowrap ${textMuted} ${borderColor} ${outlineBtnHover}`}
          >
            oopbuy spreadsheet
          </button>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            buy it now
          </a>
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${textColor}`}
        >
          <i className={`text-xl ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col gap-3 max-h-[80vh] overflow-y-auto">
          <Link
            to="/"
            onClick={() => setIsMobileMenuOpen(false)}
            className="w-full text-left px-3 py-2.5 text-sm font-medium text-foreground-900 rounded-md cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line mr-2"></i>
            Home
          </Link>

          <Link
            to="/article/oopbuy-spreadsheet-complete-guide-2026"
            onClick={() => setIsMobileMenuOpen(false)}
            className="w-full text-left px-3 py-2.5 text-sm font-medium text-foreground-900 rounded-md cursor-pointer whitespace-nowrap"
          >
            <i className="ri-book-open-line mr-2"></i>
            Shopping Guide
          </Link>

          <div>
            <button
              onClick={() => setMobileCatExpanded(!mobileCatExpanded)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-foreground-700 uppercase tracking-wider cursor-pointer"
            >
              <span>Categories</span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatExpanded ? "rotate-180" : ""}`}></i>
            </button>
            {mobileCatExpanded && (
              <div className="grid grid-cols-2 gap-1 mt-1">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="w-full text-left px-3 py-2 text-xs text-foreground-700 hover:bg-background-100 rounded-md transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0 bg-background-200">
                      <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="truncate">{cat.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 pt-2 border-t border-background-200/70">
            <button
              onClick={() => {
                window.open("https://kaspreadsheet.com/", "_blank", "noopener,noreferrer");
                setIsMobileMenuOpen(false);
              }}
              className="w-full px-4 py-2.5 text-sm font-medium text-foreground-700 border border-background-300 rounded-md cursor-pointer whitespace-nowrap"
            >
              oopbuy spreadsheet
            </button>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-full px-5 py-2.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-md text-center cursor-pointer whitespace-nowrap"
            >
              buy it now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}